import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence, useScroll, useSpring } from "framer-motion";
import {
  FiSearch,
  FiUpload,
  FiTrash2,
  FiPlay,
  FiFilm,
  FiExternalLink,
  FiX,
  FiVideo,
  FiClock,
  FiUser,
  FiMail,
  FiArrowUpRight,
  FiYoutube,
} from "react-icons/fi";
import { FaGithub, FaLinkedin, FaInstagram, FaWhatsapp } from "react-icons/fa";
import { SiLeetcode } from "react-icons/si";
import { db } from "./firebase";
import { collection, addDoc, getDocs, deleteDoc, doc } from "firebase/firestore";
import Navbar from "./Navbar";
import Foot from "./Foot";
import "./Hero.css"; // design system: tokens, buttons, grain, .kk-progress
import "./Video.css"; // everything unique to this page

/* ═══════════════ DATA (presentational only — logic untouched) ═══════════════ */

const VIDEO_TABS = ["All", "YouTube", "Instagram", "Facebook", "Other"];

// Local posters used when a remote thumbnail fails to load.
const FALLBACK_IMG = [
  "/img/proj-notes.jpg",
  "/img/about-workspace.jpg",
  "/img/proj-listing.jpg",
  "/img/cert-hackathon.jpg",
];

// Demo items for preview when Firebase is not configured.
// (In your project with real `db`, these are never used — fetchVideos loads Firestore.)
const DEMO_VIDEOS = [
  { id: "demo-1", title: "JavaScript Event Loop Explained", file: "https://www.youtube.com/watch?v=8aGhZQkoFbQ", email: "karanksxxx@gmail.com", date: new Date().toLocaleString() },
  { id: "demo-2", title: "React Full Course for Beginners", file: "https://youtu.be/bMknfKXIFA8", email: "karanksxxx@gmail.com", date: new Date().toLocaleString() },
  { id: "demo-3", title: "Neural Networks Demystified", file: "https://www.youtube.com/watch?v=aircAruvnKk", email: "karanksxxx@gmail.com", date: new Date().toLocaleString() },
  { id: "demo-4", title: "Learn Python Full Course", file: "https://youtu.be/rfscVS0vtbw", email: "karanksxxx@gmail.com", date: new Date().toLocaleString() },
];

const SOCIALS = [
  { icon: <FaGithub />, href: "https://github.com/Karanks1436", label: "GitHub" },
  { icon: <FaLinkedin />, href: "https://linkedin.com", label: "LinkedIn" },
  { icon: <FaInstagram />, href: "https://www.instagram.com/karanks1436", label: "Instagram" },
  { icon: <FiMail />, href: "mailto:karanksxxx@gmail.com", label: "Email" },
  { icon: <FaWhatsapp />, href: "https://wa.me/919041429065", label: "WhatsApp" },
  { icon: <SiLeetcode />, href: "https://leetcode.com/u/karanks1436/", label: "LeetCode" },
];

const tabPlatform = (url) => {
  if (!url) return "other";
  if (url.includes("youtube.com") || url.includes("youtu.be")) return "youtube";
  if (url.includes("instagram.com")) return "instagram";
  if (url.includes("facebook.com")) return "facebook";
  return "other";
};

/* ═══════════════ ONE rAF LOOP — [data-plx] ═══════════════ */

function useParallax(rootRef) {
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const items = Array.from(root.querySelectorAll("[data-plx]"));
    let raf = 0;

    const render = () => {
      const vh = window.innerHeight;
      for (const el of items) {
        const speed = parseFloat(el.dataset.plx) || 0;
        const r = el.getBoundingClientRect();
        if (r.bottom < -420 || r.top > vh + 420) continue;
        const y = (r.top + r.height / 2 - vh / 2) * -speed;
        el.style.transform = `translate3d(0, ${y.toFixed(2)}px, 0)`;
      }
      raf = requestAnimationFrame(render);
    };
    raf = requestAnimationFrame(render);
    return () => cancelAnimationFrame(raf);
  }, [rootRef]);
}

// ─── Floating Particles ────────────────────────────────────────
function VideoParticles() {
  const parts = useRef(
    Array.from({ length: 18 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      s: Math.random() * 3 + 1,
      d: Math.random() * 12 + 8,
      dl: Math.random() * 5,
    }))
  );
  return (
    <div className="vd-particles" aria-hidden="true">
      {parts.current.map((p) => (
        <motion.span
          key={p.id}
          className="vd-particle"
          style={{ left: `${p.x}%`, top: `${p.y}%`, width: p.s, height: p.s }}
          animate={{ y: [0, -20, 0], opacity: [0.08, 0.4, 0.08] }}
          transition={{ duration: p.d, repeat: Infinity, delay: p.dl, ease: "easeInOut" }}
        />
      ))}
    </div>
  );
}

// ─── Video Card ────────────────────────────────────────────────
function VideoCard({ video, index, user, onDelete }) {
  const canDelete = user && video.email === user.Email;
  const localFallback = FALLBACK_IMG[index % FALLBACK_IMG.length];

  const getPlatform = (url) => {
    if (!url) return "unknown";
    if (url.includes("youtube.com") || url.includes("youtu.be")) return "youtube";
    if (url.includes("instagram.com")) return "instagram";
    if (url.includes("facebook.com")) return "facebook";
    return "other";
  };

  const getEmbedURL = (url) => {
    const platform = getPlatform(url);
    if (platform === "youtube") {
      const videoId = url.includes("youtube.com")
        ? new URL(url).searchParams.get("v")
        : url.split("/").pop();
      // Autoplay muted (browsers only allow autoplay when muted)
      return `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&rel=0`;
    }
    if (platform === "instagram") {
      const match = url.match(/instagram\.com\/(p|tv|reel)\/([^/?]+)/);
      if (match && match[2]) return `https://www.instagram.com/p/${match[2]}/embed`;
      return url;
    }
    return url;
  };

  const getThumbnail = (url) => {
    const platform = getPlatform(url);
    if (platform === "youtube") {
      const videoId = url.includes("youtube.com")
        ? new URL(url).searchParams.get("v")
        : url.split("/").pop();
      return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
    }
    return null;
  };

  const platform = getPlatform(video.file);
  const thumbnail = getThumbnail(video.file);

  return (
    <motion.div
      className="vd-card"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ delay: (index % 3) * 0.08, duration: 0.5 }}
      whileHover={{ y: -6 }}
    >
      {/* Embed */}
      <div className="vd-embed">
        {video.file ? (
          platform === "youtube" || platform === "instagram" ? (
            <iframe
              src={getEmbedURL(video.file)}
              frameBorder="0"
              allowFullScreen
              allow="encrypted-media; autoplay"
              title={video.title}
              className="vd-iframe"
              loading="lazy"
            />
          ) : (
            <a
              href={video.file}
              target="_blank"
              rel="noopener noreferrer"
              className="vd-ext"
            >
              {thumbnail ? (
                <img
                  src={thumbnail}
                  alt={video.title}
                  className="vd-thumb"
                  onError={(e) => {
                    if (!e.currentTarget.dataset.fb) {
                      e.currentTarget.dataset.fb = "1";
                      e.currentTarget.src = localFallback;
                    }
                  }}
                />
              ) : (
                <div className="vd-placeholder">
                  <FiPlay />
                </div>
              )}
              <div className="vd-play">
                <FiPlay />
                <span>Watch Video</span>
              </div>
            </a>
          )
        ) : (
          <div className="vd-placeholder">
            <FiVideo />
            <span>No video available</span>
          </div>
        )}

        {/* Platform badge */}
        <span className={`vd-badge ${platform}`}>
          <FiFilm />
          {platform === "youtube" && "YouTube"}
          {platform === "instagram" && "Instagram"}
          {platform === "facebook" && "Facebook"}
          {platform === "other" && "Video"}
        </span>

        <span className="vd-no">{String(index + 1).padStart(2, "0")}</span>
      </div>

      {/* Body */}
      <div className="vd-body">
        <h3 className="vd-title">{video.title}</h3>

        <div className="vd-meta">
          <span>
            <FiClock /> {video.date}
          </span>
        </div>

        <div className="vd-actions">
          {video.file && platform === "other" && (
            <motion.a
              href={video.file}
              target="_blank"
              rel="noopener noreferrer"
              className="kk-btn kk-btn--ghost vd-btn"
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
            >
              <FiExternalLink /> Open
            </motion.a>
          )}

          {canDelete && (
            <motion.button
              className="kk-btn kk-btn--ghost vd-btn vd-btn--danger"
              onClick={() => onDelete(video.id)}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              type="button"
            >
              <FiTrash2 /> Delete
            </motion.button>
          )}
        </div>
      </div>
    </motion.div>
  );
}

// ─── Admin Upload Form ─────────────────────────────────────────
function AdminUploadForm({ videoTitle, videoURL, onTitleChange, onURLChange, onSubmit }) {
  return (
    <motion.section
      className="vd-upload"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
    >
      <div className="vd-upload__head">
        <span className="kk-eyebrow">Admin — Media</span>
        <h2 className="kk-h2">
          Add a new <span className="kk-serif kk-acid">video</span>
        </h2>
        <p className="vd-upload__note">
          Paste a YouTube, Instagram or Facebook link — the player is generated automatically.
        </p>
      </div>

      <form onSubmit={onSubmit} className="vd-form">
        <div className="vd-form__row">
          <label className="vd-field" style={{ flex: 1 }}>
            <span>
              <FiFilm /> Video title
            </span>
            <input
              type="text"
              value={videoTitle}
              onChange={onTitleChange}
              placeholder="Enter video title"
              required
            />
          </label>

          <label className="vd-field" style={{ flex: 1 }}>
            <span>
              <FiExternalLink /> Video URL
            </span>
            <input
              type="text"
              value={videoURL}
              onChange={onURLChange}
              placeholder="https://youtube.com/watch?v=..."
              required
            />
          </label>
        </div>

        <div className="vd-chips">
          <span className="vd-supported">Supported</span>
          <span className="vd-chip youtube">YouTube</span>
          <span className="vd-chip instagram">Instagram</span>
          <span className="vd-chip facebook">Facebook</span>
        </div>

        <div className="vd-submit-row">
          <motion.button
            type="submit"
            className="kk-btn vd-submit"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <FiUpload /> Upload video
          </motion.button>
          <span className="vd-submit__hint">
            <FiYoutube /> Stored in the <code>Videos</code> collection
          </span>
        </div>
      </form>
    </motion.section>
  );
}

// ─── Empty State ───────────────────────────────────────────────
function EmptyState({ searchTerm }) {
  return (
    <motion.div className="vd-empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <div className="vd-empty__ic">
        <FiVideo />
      </div>
      <h3>{searchTerm ? "No videos found" : "No videos yet"}</h3>
      <p>
        {searchTerm ? (
          <>
            No results for &ldquo;<strong>{searchTerm}</strong>&rdquo; — try another keyword.
          </>
        ) : (
          "Videos will appear here once added."
        )}
      </p>
    </motion.div>
  );
}

// ─── Main Video Component ──────────────────────────────────────
// LOGIC: identical to your original — same state, same handlers, same
// Firebase calls. Only styling/classes, parallax and a preview fallback added.
export default function Video() {
  const rootRef = useRef(null);
  const [activeTab, setActiveTab] = useState("All");
  const [videos, setVideos] = useState([]);
  const [videoURL, setVideoURL] = useState("");
  const [videoTitle, setVideoTitle] = useState("");
  const [user, setUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);

  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, { stiffness: 120, damping: 28 });

  useParallax(rootRef);

  const authorEmail = "karanksxxx@gmail.com";
  const isAuthor = user && user.Email === authorEmail;

  const fetchVideos = async () => {
    if (!db) {
      setTimeout(() => {
        setVideos(DEMO_VIDEOS);
        setLoading(false);
      }, 600);
      return;
    }
    try {
      setLoading(true);
      const videoCollection = collection(db, "Videos");
      const videoSnapshot = await getDocs(videoCollection);
      const videoList = videoSnapshot.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      }));
      setVideos(videoList);
    } catch (error) {
      console.error("Error fetching videos:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem("user"));
    if (storedUser) {
      setUser(storedUser);
    }
    fetchVideos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isValidVideoURL = (url) => {
    return (
      url &&
      (url.includes("youtube.com") ||
        url.includes("youtu.be") ||
        url.includes("instagram.com") ||
        url.includes("facebook.com"))
    );
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!videoURL || !videoTitle) {
      alert("Please enter both video URL and title.");
      return;
    }
    if (!isValidVideoURL(videoURL)) {
      alert("Invalid URL. Please enter a valid YouTube, Instagram, or Facebook link.");
      return;
    }
    if (!db) {
      setVideos((p) => [
        {
          id: "local-" + Date.now(),
          title: videoTitle,
          file: videoURL,
          email: user?.Email || "demo",
          date: new Date().toLocaleString(),
        },
        ...p,
      ]);
      setVideoURL("");
      setVideoTitle("");
      return;
    }
    try {
      await addDoc(collection(db, "Videos"), {
        title: videoTitle,
        file: videoURL,
        email: user.Email,
        date: new Date().toLocaleString(),
      });
      fetchVideos();
      setVideoURL("");
      setVideoTitle("");
    } catch (error) {
      console.error("Error uploading video:", error);
    }
  };

  const handleDelete = async (id) => {
    const videoToDelete = videos.find((video) => video.id === id);
    if (videoToDelete && user && videoToDelete.email === user.Email) {
      if (!db || String(id).startsWith("demo-") || String(id).startsWith("local-")) {
        setVideos((p) => p.filter((v) => v.id !== id));
        return;
      }
      try {
        await deleteDoc(doc(db, "Videos", id));
        fetchVideos();
      } catch (error) {
        console.error("Error deleting video:", error);
      }
    }
  };

  const filteredVideos = videos.filter((video) => {
    const matchSearch = video.title.toLowerCase().includes(searchTerm.toLowerCase());
    if (activeTab === "All") return matchSearch;
    return matchSearch && tabPlatform(video.file) === activeTab.toLowerCase();
  });

  return (
    <div className="kk-root vd-page" ref={rootRef}>
      <motion.div className="kk-progress" style={{ scaleX: progress }} />
      <VideoParticles />

      

      {/* ══════════════ HERO ══════════════ */}
      <header className="vd-hero">
        <div className="vd-hero__bg" aria-hidden="true">
          <span className="vd-hero__grid" data-plx="0.05" />
          <span className="vd-hero__glow vd-hero__glow--acid" data-plx="0.16" />
          <span className="vd-hero__glow vd-hero__glow--violet" data-plx="0.24" />
          <span className="vd-hero__word" data-plx="0.1">
            SCREEN
          </span>
        </div>

        <div className="kk-wrap vd-hero__in">
          <motion.div
            className="vd-hero__copy"
            initial={{ opacity: 0, y: 26 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="vd-kicker">
              <span className="kk-dot" />
              <span>Video</span>
              <span className="kk-slash">/</span>
              <span>Media</span>
            </div>

            <h1 className="vd-title">
              <motion.span
                className="vd-title__line"
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.9, delay: 0.05, ease: [0.22, 1, 0.36, 1] }}
              >
                Video <span className="kk-serif kk-acid">Gallery</span>
              </motion.span>
              <motion.span
                className="vd-title__sub"
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.9, delay: 0.14, ease: [0.22, 1, 0.36, 1] }}
              >
                watch, learn, repeat.
              </motion.span>
            </h1>

            <p className="vd-lead">
              Watch tutorials, projects and creative content from various platforms —
              everything plays right here, no redirects.
            </p>

            {user && (
              <motion.div
                className="vd-greet"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                <FiUser /> Logged in as <strong>{user.Name}</strong>
              </motion.div>
            )}

            <div className="vd-hero__meta">
              <div>
                <b>{videos.length || 4}</b>
                <span>Videos</span>
              </div>
              <div>
                <b>05</b>
                <span>Sources</span>
              </div>
              <div>
                <b>HD</b>
                <span>Quality</span>
              </div>
            </div>
          </motion.div>

          <motion.aside
            className="vd-hero__panel"
            initial={{ opacity: 0, y: 26 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.16 }}
          >
            <div className="vd-hero__panel-top">
              <span className="vd-hero__panel-dot" />
              <span>Screen Index</span>
              <span className="vd-hero__panel-code">KKS·1436</span>
            </div>

            <div className="vd-hero__panel-num">
              <b>{String(videos.length || 4).padStart(2, "0")}</b>
              <span>
                videos
                <br />
                on air
              </span>
            </div>

            <dl className="vd-hero__panel-rows">
              <div>
                <dt>Sources</dt>
                <dd>YouTube · IG · FB</dd>
              </div>
              <div>
                <dt>Player</dt>
                <dd>Inline embed</dd>
              </div>
              <div>
                <dt>Upload</dt>
                <dd>Author only</dd>
              </div>
              <div>
                <dt>Sync</dt>
                <dd>Firestore live</dd>
              </div>
            </dl>
          </motion.aside>
        </div>
      </header>

      {/* ══════════════ TOOLBAR ══════════════ */}
      <div className="vd-bar">
        <div className="kk-wrap vd-bar__in">
          <div className="vd-tabs">
            {VIDEO_TABS.map((t) => (
              <button
                key={t}
                className={`vd-tab ${activeTab === t ? "is-active" : ""}`}
                onClick={() => setActiveTab(t)}
                type="button"
              >
                {t}
              </button>
            ))}
          </div>

          <div className="vd-search">
            <FiSearch className="vd-search__ic" />
            <input
              type="text"
              placeholder="Search videos..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <motion.button
                className="vd-search__x"
                onClick={() => setSearchTerm("")}
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                aria-label="Clear search"
                type="button"
              >
                <FiX />
              </motion.button>
            )}
          </div>

          <span className="vd-count">
            <FiFilm /> {filteredVideos.length} videos
          </span>
        </div>
      </div>

      {/* ══════════════ MAIN ══════════════ */}
      <main className="kk-section kk-tight vd-main">
        <div className="kk-wrap">
          {/* Admin form */}
          {isAuthor && (
            <AdminUploadForm
              videoTitle={videoTitle}
              videoURL={videoURL}
              onTitleChange={(e) => setVideoTitle(e.target.value)}
              onURLChange={(e) => setVideoURL(e.target.value)}
              onSubmit={handleUpload}
            />
          )}

          <div className="vd-sec-head">
            <div>
              <span className="kk-eyebrow kk-eyebrow--violet">02 — Screen</span>
              <h2 className="kk-h2">
                Available
                <br />
                <span className="kk-serif">videos</span>
              </h2>
            </div>
            <div className="vd-sec-head__aside">
              <p>
                Tutorials, walkthroughs and clips — embedded straight from the source,
                so you never lose your place.
              </p>
            </div>
          </div>

          {loading ? (
            <div className="vd-grid">
              {Array.from({ length: 6 }).map((_, i) => (
                <motion.div
                  key={i}
                  className="vd-skel"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: i * 0.08 }}
                >
                  <div className="vd-skel__img" />
                  <div className="vd-skel__body">
                    <div className="vd-skel__line" />
                    <div className="vd-skel__line vd-skel__line--sm" />
                  </div>
                </motion.div>
              ))}
            </div>
          ) : filteredVideos.length === 0 ? (
            <EmptyState searchTerm={searchTerm} />
          ) : (
            <div className="vd-grid">
              {filteredVideos.map((video, index) => (
                <VideoCard
                  key={video.id}
                  video={video}
                  index={index}
                  user={user}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          )}
        </div>
      </main>

      {/* ══════════════ CTA ══════════════ */}
      <section className="vd-cta">
        <div className="vd-cta__orbs" aria-hidden="true">
          <span data-plx="0.1" />
          <span data-plx="0.18" />
        </div>

        <div className="kk-wrap vd-cta__in">
          <span className="kk-eyebrow">More on the channel</span>
          <h2 className="kk-h2">
            Want a video on{" "}
            <span className="kk-serif kk-acid">a topic?</span>
          </h2>
          <p>Tell me what to record — a concept, a build, or a walkthrough of the code.</p>

          <div className="vd-cta__btns">
            <a className="kk-btn kk-btn--lg" href="mailto:karanksxxx@gmail.com?subject=Video%20request">
              <FiMail /> Request a video
            </a>
            <a
              className="kk-btn kk-btn--ghost kk-btn--lg"
              href="https://www.youtube.com/@Karanks1436"
              target="_blank"
              rel="noreferrer"
            >
              <FiYoutube /> Open channel
            </a>
          </div>

          <div className="vd-cta__socials">
            {SOCIALS.map((s) => (
              <a
                key={s.label}
                href={s.href}
                target={s.href.startsWith("mailto:") ? undefined : "_blank"}
                rel="noreferrer"
                aria-label={s.label}
                title={s.label}
              >
                {s.icon}
              </a>
            ))}
          </div>
        </div>
      </section>
   <Navbar />
      <Foot />

      <a
        className="kk-wa"
        href="https://wa.me/919041429065"
        target="_blank"
        rel="noreferrer"
        title="Chat on WhatsApp"
      >
        <FaWhatsapp />
      </a>
    </div>
  );
}
